import type { Frame, WordCue } from "./types";

const HEADER_SIZE = 5; // 1 byte kind + 4 bytes length
const KIND_AUDIO = 0x01;
const KIND_WORDS = 0x02;

const decoder = new TextDecoder();

export class FrameParser {
  private buffer: Uint8Array = new Uint8Array(0);

  /**
   * Feed new bytes from the HTTP stream. Returns all complete frames
   * that can be decoded from the internal buffer. Leftover bytes
   * (incomplete frame header or payload) are retained for the next call.
   */
  feed(chunk: Uint8Array): Frame[] {
    // Concatenate new chunk onto existing buffer
    if (chunk.length > 0) {
      const combined = new Uint8Array(this.buffer.length + chunk.length);
      combined.set(this.buffer, 0);
      combined.set(chunk, this.buffer.length);
      this.buffer = combined;
    }

    const frames: Frame[] = [];

    while (this.buffer.length >= HEADER_SIZE) {
      const kind = this.buffer[0];
      const view = new DataView(this.buffer.buffer, this.buffer.byteOffset, this.buffer.length);
      const length = view.getUint32(1, /* little-endian */ true);

      if (this.buffer.length < HEADER_SIZE + length) {
        // Not enough data yet — wait for more
        break;
      }

      const payload = this.buffer.slice(HEADER_SIZE, HEADER_SIZE + length);
      // Advance buffer past this frame
      this.buffer = this.buffer.slice(HEADER_SIZE + length);

      if (kind === KIND_AUDIO) {
        frames.push({ kind: "audio", payload });
      } else if (kind === KIND_WORDS) {
        const json = decoder.decode(payload);
        const cues = JSON.parse(json) as WordCue[];
        frames.push({ kind: "words", cues });
      }
      // Unknown kinds are silently skipped
    }

    return frames;
  }

  /** Discard any buffered bytes. Call when aborting or resetting. */
  reset(): void {
    this.buffer = new Uint8Array(0);
  }
}
